import { action, computed, makeObservable, observable, runInAction } from "mobx";
import { onAuthStateChanged, type User } from "firebase/auth";
import { collection, deleteDoc, doc, getDocs, getFirestore, orderBy, query, runTransaction, serverTimestamp, setDoc } from "firebase/firestore";
import type { Logic } from "../../logic";
import { auth, signInWithGoogle, signOutUser } from "../../auth";

export class NavBarLogic {
  user: User | null = null;
  authReady = false;
  authInProgress = false;
  authError: string | undefined;
  likedStampIds = new Set<string>();
  likingStampIds = new Set<string>();
  likesLoading = false;
  ownedStampQuantities = new Map<string, number>();
  ownedStampIdsUpdating = new Set<string>();
  ownedStampsLoading = false;
  private resolveAuthReady?: () => void;
  private readonly authReadyPromise: Promise<void>;

  constructor(private _logic: Logic) {
    this.authReadyPromise = new Promise((resolve) => {
      this.resolveAuthReady = resolve;
    });
    makeObservable(this, {
      user: observable.ref,
      authReady: observable,
      authInProgress: observable,
      authError: observable,
      likedStampIds: observable.ref,
      likingStampIds: observable.ref,
      likesLoading: observable,
      ownedStampQuantities: observable.ref,
      ownedStampIdsUpdating: observable.ref,
      ownedStampsLoading: observable,
      logged: computed,
      displayName: computed,
      initials: computed,
      signIn: action,
      signOut: action,
      toggleLike: action,
      adjustOwnedQuantity: action,
    });

    onAuthStateChanged(
      auth,
      (user) => {
        runInAction(() => {
          this.user = user;
          this.authReady = true;
          this.resolveAuthReady?.();
          if (user) this.authError = undefined;
          this.likedStampIds = new Set();
          this.likesLoading = user !== null;
          this.ownedStampQuantities = new Map();
          this.ownedStampsLoading = user !== null;
        });
        if (user) {
          void this.loadLikedStampIds(user.uid);
          void this.loadOwnedStampQuantities(user.uid);
        }
      },
      () => {
        runInAction(() => {
          this.user = null;
          this.authReady = true;
          this.resolveAuthReady?.();
          this.authError = "We could not restore your sign-in session.";
          this.likedStampIds = new Set();
          this.likesLoading = false;
          this.ownedStampQuantities = new Map();
          this.ownedStampsLoading = false;
        });
      }
    );
  }

  get logged() {
    return this.user !== null;
  }

  get displayName() {
    return this.user?.displayName || this.user?.email || "Collector";
  }

  get initials() {
    return this.displayName
      .split(/\s+/)
      .slice(0, 2)
      .map((part) => part.charAt(0).toUpperCase())
      .join("");
  }

  async signIn() {
    this.authInProgress = true;
    this.authError = undefined;

    try {
      await signInWithGoogle();
      return true;
    } catch (error) {
      runInAction(() => {
        this.authError = getSignInErrorMessage(error);
      });
      return false;
    } finally {
      runInAction(() => {
        this.authInProgress = false;
      });
    }
  }

  async signOut() {
    this.authInProgress = true;
    this.authError = undefined;

    try {
      await signOutUser();
    } catch (error) {
      runInAction(() => {
        this.authError = "We could not sign you out. Please try again.";
      });
      console.error("Google sign-out failed", error);
    } finally {
      runInAction(() => {
        this.authInProgress = false;
      });
    }
  }

  isLiked(stampId?: string) {
    return Boolean(stampId && this.likedStampIds.has(stampId));
  }

  isLikeUpdating(stampId?: string) {
    return Boolean(stampId && this.likingStampIds.has(stampId));
  }

  getOwnedQuantity(stampId?: string) {
    return stampId ? this.ownedStampQuantities.get(stampId) ?? 0 : 0;
  }

  isOwnedQuantityUpdating(stampId?: string) {
    return Boolean(stampId && this.ownedStampIdsUpdating.has(stampId));
  }

  async toggleLike(stampId?: string) {
    const user = await this.waitForAuth();
    if (!user || !stampId) return false;

    const likeRef = doc(getFirestore(), "users", user.uid, "likes", stampId);
    const currentlyLiked = this.isLiked(stampId);
    runInAction(() => {
      const nextIds = new Set(this.likedStampIds);
      currentlyLiked ? nextIds.delete(stampId) : nextIds.add(stampId);
      this.likedStampIds = nextIds;
      this.likingStampIds = new Set(this.likingStampIds).add(stampId);
    });

    try {
      if (currentlyLiked) {
        await deleteDoc(likeRef);
      } else {
        await setDoc(likeRef, { stampId, createdAt: serverTimestamp() });
      }
      return true;
    } catch (error) {
      runInAction(() => {
        const revertedIds = new Set(this.likedStampIds);
        currentlyLiked ? revertedIds.add(stampId) : revertedIds.delete(stampId);
        this.likedStampIds = revertedIds;
        this.authError = "We could not update this liked stamp. Please try again.";
      });
      console.error("Updating liked stamp failed", error);
      return false;
    } finally {
      runInAction(() => {
        const nextUpdatingIds = new Set(this.likingStampIds);
        nextUpdatingIds.delete(stampId);
        this.likingStampIds = nextUpdatingIds;
      });
    }
  }

  async getLikedStampIds() {
    const user = await this.waitForAuth();
    if (!user) return [];

    return this.loadLikedStampIds(user.uid);
  }

  async getOwnedStampIds() {
    const user = await this.waitForAuth();
    if (!user) return [];

    return this.loadOwnedStampQuantities(user.uid);
  }

  async adjustOwnedQuantity(stampId: string | undefined, change: 1 | -1) {
    const user = await this.waitForAuth();
    if (!user || !stampId) return false;

    const previousQuantity = this.getOwnedQuantity(stampId);
    const nextQuantity = Math.max(0, previousQuantity + change);
    if (nextQuantity === previousQuantity) return true;

    runInAction(() => {
      const quantities = new Map(this.ownedStampQuantities);
      nextQuantity === 0 ? quantities.delete(stampId) : quantities.set(stampId, nextQuantity);
      this.ownedStampQuantities = quantities;
      this.ownedStampIdsUpdating = new Set(this.ownedStampIdsUpdating).add(stampId);
    });

    try {
      const ownedRef = doc(getFirestore(), "users", user.uid, "owned", stampId);
      await runTransaction(getFirestore(), async (transaction) => {
        const snapshot = await transaction.get(ownedRef);
        const storedQuantity = Number(snapshot.data()?.quantity) || 0;
        const updatedQuantity = Math.max(0, storedQuantity + change);

        if (updatedQuantity === 0) {
          transaction.delete(ownedRef);
        } else {
          transaction.set(ownedRef, {
            stampId,
            quantity: updatedQuantity,
            updatedAt: serverTimestamp(),
          });
        }
      });
      return true;
    } catch (error) {
      runInAction(() => {
        const quantities = new Map(this.ownedStampQuantities);
        previousQuantity === 0 ? quantities.delete(stampId) : quantities.set(stampId, previousQuantity);
        this.ownedStampQuantities = quantities;
        this.authError = "We could not update your owned stamp quantity. Please try again.";
      });
      console.error("Updating owned stamp quantity failed", error);
      return false;
    } finally {
      runInAction(() => {
        const updatingIds = new Set(this.ownedStampIdsUpdating);
        updatingIds.delete(stampId);
        this.ownedStampIdsUpdating = updatingIds;
      });
    }
  }

  private async loadLikedStampIds(userId: string) {
    try {
      const snapshot = await getDocs(
        query(collection(getFirestore(), "users", userId, "likes"), orderBy("createdAt", "desc"))
      );
      const stampIds = snapshot.docs.map((like) => like.id);
      runInAction(() => {
        if (this.user?.uid === userId) this.likedStampIds = new Set(stampIds);
      });
      return stampIds;
    } catch (error) {
      runInAction(() => {
        if (this.user?.uid === userId) {
          this.authError = "We could not load your liked stamps.";
        }
      });
      console.error("Loading liked stamps failed", error);
      return [];
    } finally {
      runInAction(() => {
        if (this.user?.uid === userId) this.likesLoading = false;
      });
    }
  }

  private async loadOwnedStampQuantities(userId: string) {
    try {
      const snapshot = await getDocs(
        query(collection(getFirestore(), "users", userId, "owned"), orderBy("updatedAt", "desc"))
      );
      const quantities = new Map(
        snapshot.docs
          .map((owned) => [owned.id, Number(owned.data().quantity) || 0] as const)
          .filter(([, quantity]) => quantity > 0)
      );
      runInAction(() => {
        if (this.user?.uid === userId) this.ownedStampQuantities = quantities;
      });
      return Array.from(quantities.keys());
    } catch (error) {
      runInAction(() => {
        if (this.user?.uid === userId) {
          this.authError = "We could not load your owned stamps.";
        }
      });
      console.error("Loading owned stamps failed", error);
      return [];
    } finally {
      runInAction(() => {
        if (this.user?.uid === userId) this.ownedStampsLoading = false;
      });
    }
  }

  private async waitForAuth() {
    if (!this.authReady) await this.authReadyPromise;
    return this.user;
  }
}

function getSignInErrorMessage(error: unknown) {
  const code = (error as { code?: string }).code;

  if (code === "auth/popup-closed-by-user") {
    return "The Google sign-in window was closed before you finished.";
  }
  if (code === "auth/popup-blocked") {
    return "Your browser blocked the sign-in window. Please allow pop-ups and try again.";
  }

  console.error("Google sign-in failed", error);
  return "We could not sign you in with Google. Please try again.";
}
